import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Input, InputNumber, Space, message, Select, Checkbox, DatePicker, Alert } from 'antd';
import {
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import enLocale from 'antd/es/date-picker/locale/en_US';
import axios from 'axios';
import authService from '../utils/auth';
import { openInvoicePrintWindow, INTERNATIONAL_BANK } from '../utils/invoiceTemplate';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const FROM_COMPANY = 'Worldwide Holidays Limited';
const GST_NO = '70-820-390';
const PHONE = '0800 116688';
const WEBSITE = 'wanguo.co.nz';
const BRAND = '#0085E7';
const BRAND_DARK = '#005fa3';

const BASE_FS = 13;
const cellTh = { padding: '10px 12px', fontSize: BASE_FS, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, background: BRAND, whiteSpace: 'nowrap' };
const cellTd = { padding: '8px 10px', fontSize: BASE_FS, color: '#333', borderBottom: '1px solid #e8ecf0' };
const inputBorder = { border: '1px solid #d9d9d9', borderRadius: 4, boxShadow: 'none', background: '#fff', fontSize: BASE_FS };

const newItem = () => ({ description: '', quantity: 1, amount: 0, gst: false });

export default function InvoiceFormModal({ open, onClose, onSaved, invoice }) {
  const userInfo = authService.getUserInfo();
  const isEdit = !!invoice;

  const [invoiceNo, setInvoiceNo] = useState('');
  const [reference, setReference] = useState('');
  const [particulars, setParticulars] = useState('');
  const [notes, setNotes] = useState('');
  const [billToName, setBillToName] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [billToPhone, setBillToPhone] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [gstPercent, setGstPercent] = useState(15);
  const [amountPaid, setAmountPaid] = useState(0);
  const [items, setItems] = useState([newItem()]);
  const [saving, setSaving] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(dayjs());
  const [dueDate, setDueDate] = useState(dayjs().add(7, 'day'));
  const [showDueDate, setShowDueDate] = useState(true);
  const [paymentMode, setPaymentMode] = useState('domestic'); // 'domestic' | 'international'
  // 银行信息只能来自门店；拿不到就是 null，绝不能用任何默认账号兜底（会导致客户付错款）
  const [bankInfo, setBankInfo] = useState(null);

  const salesName = userInfo?.name || '';
  const salesEmail = userInfo?.email || '';
  // 销售电话：登录时缓存的 userInfo 里没有这个字段，统一从 /auth/me 实时取（见下方 effect）
  const [salesPhone, setSalesPhone] = useState('');
  const hasBank = !!bankInfo?.bankAccountNo;

  // 获取收款账号（从 /auth/me 实时取，不依赖 localStorage 缓存）。
  // 用后端下发的 invoiceStoreId 而不是 storeId：账号跟人走，
  // 销售调去别的门店（或改成在家办公）时收款账号不变，规则在后端一处维护
  useEffect(() => {
    if (!open) return;
    // 编辑已有 invoice 时用当初保存的银行快照，不被门店当前配置覆盖
    // 编辑已有发票：销售电话用开票当时的快照，不被销售现在的号码覆盖
    setSalesPhone(invoice?.salesRepPhone || '');
    if (invoice?.bankInfo?.bankAccountNo) { setBankInfo(invoice.bankInfo); return; }
    setBankInfo(null);
    const headers = authService.getAuthHeaders();
    axios.get(`${API_BASE}/auth/me`, { headers })
      .then(meRes => {
        const me = meRes.data?.data || {};
        // ⚠ 必须取在 storeId 早退之前：没配门店的销售照样要在发票上显示电话。
        // phoneDirect 是「客户直拨号」，正是该给客户的号；没配才回退通用 phone。
        if (!invoice?.salesRepPhone) setSalesPhone(me.phoneDirect || me.phone || '');
        /*
         * 收款账号由后端解析好下发（me.invoiceBank）。
         * 2026-10-08 删掉了原来「按门店查账号」的回落 —— 账号已是独立实体，
         * stores 上的银行字段也一并删了，再按门店查只会查到空。
         * 取不到就不显示账户信息，宁可空着也不印猜出来的号码。
         */
        // 新发票附上当前国际汇款账户快照；已有发票沿用保存时的快照（上方早退）
        if (me.invoiceBank && me.invoiceBank.bankAccountNo) setBankInfo({ ...me.invoiceBank, international: INTERNATIONAL_BANK });
      })
      .catch(() => setBankInfo(null));
  }, [open, invoice]);

  useEffect(() => {
    if (!open) return;
    if (invoice) {
      setInvoiceNo(invoice.invoiceNo || '');
      setReference(invoice.reference || '');
      setParticulars(invoice.particulars || '');
      setNotes(invoice.notes || '');
      setBillToName(invoice.billToName || '');
      setBillToEmail(invoice.billToEmail || '');
      setBillToPhone(invoice.billToPhone || '');
      setBillToAddress(invoice.billToAddress || '');
      setGstPercent(Number(invoice.gstPercent) || 15);
      setAmountPaid(Number(invoice.amountPaid) || 0);
      setInvoiceDate(invoice.invoiceDate ? dayjs(invoice.invoiceDate) : dayjs());
      setDueDate(invoice.dueDate ? dayjs(invoice.dueDate) : dayjs().add(7, 'day'));
      setShowDueDate(!!invoice.dueDate);
      setItems(Array.isArray(invoice.items) && invoice.items.length > 0
        ? invoice.items.map(it => ({ ...newItem(), ...it }))
        : [newItem()]);
    } else {
      setReference('');
      setParticulars('');
      setNotes('');
      setBillToName('');
      setBillToEmail('');
      setBillToPhone('');
      setBillToAddress('');
      setGstPercent(15);
      setAmountPaid(0);
      setInvoiceDate(dayjs());
      setDueDate(dayjs().add(7, 'day'));
      setShowDueDate(true);
      setItems([newItem()]);
      setInvoiceNo('');
    }
  }, [open, invoice]);

  const updateItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, i) => s + (Number(i.amount) || 0) * (Number(i.quantity) || 1), 0);
  const gstableSubtotal = items.reduce((s, i) => i.gst ? s + (Number(i.amount) || 0) * (Number(i.quantity) || 1) : s, 0);
  const gstPctNum = Number(gstPercent) || 0;
  const gstAmount = gstPctNum > 0 ? gstableSubtotal * gstPctNum / (100 + gstPctNum) : 0;
  const total = subtotal;
  const amountDue = total - (Number(amountPaid) || 0);

  const buildPayload = () => ({
    invoiceNo,
    invoiceDate: invoiceDate ? invoiceDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    dueDate: showDueDate && dueDate ? dueDate.format('YYYY-MM-DD') : null,
    billToName,
    billToEmail,
    billToPhone,
    billToAddress,
    salesRepName: salesName,
    salesRepEmail: salesEmail,
    salesRepPhone: salesPhone,
    fromCompany: FROM_COMPANY,
    gstNo: GST_NO,
    phone: PHONE,
    website: WEBSITE,
    items,
    showPassenger: false,
    gstPercent,
    amountPaid,
    reference,
    particulars,
    notes,
    paymentMode,
    bankInfo,
  });

  const handleDownload = () => {
    const ok = openInvoicePrintWindow(buildPayload());
    if (!ok) message.error('Please allow popups to download PDF');
  };

  const handleSave = async () => {
    if (!billToName.trim()) { message.warning('请输入 Bill To 名称'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API_BASE}/invoices/${invoice.id}`, buildPayload(), { headers: authService.getAuthHeaders() });
      } else {
        await axios.post(`${API_BASE}/invoices`, buildPayload(), { headers: authService.getAuthHeaders() });
      }
      message.success('Invoice 已保存');
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      message.error(err.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? '编辑 Invoice' : null}
      open={open}
      onCancel={onClose}
      width={864}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      styles={{ body: { padding: 24, minHeight: 720, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', fontSize: BASE_FS } }}
      footer={
        <Space>
          <Button onClick={onClose}>Close</Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload}>Download / Print</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save</Button>
        </Space>
      }
    >
      {/* Header: logo + INVOICE + invoice meta */}
      <div style={{ borderBottom: `3px solid ${BRAND}`, paddingBottom: 14, marginBottom: 18, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
        <img src="/logo-wanguuo.png?v=2" alt={FROM_COMPANY} style={{ width: 230 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: BRAND, letterSpacing: 1, marginBottom: 12 }}>INVOICE</div>
          <table style={{ marginLeft: 'auto', fontSize: BASE_FS }}>
            <tbody>
              <tr>
                <td style={{ color: '#888', paddingRight: 14, textAlign: 'right' }}>Invoice no.</td>
                <td style={{ textAlign: 'right', minWidth: 140 }}>
                  <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="EKOK号码" style={{ textAlign: 'right', fontWeight: 700, color: BRAND_DARK, fontSize: BASE_FS }} />
                </td>
              </tr>
              <tr>
                <td style={{ color: '#888', paddingRight: 14, textAlign: 'right' }}>Invoice date</td>
                <td style={{ textAlign: 'right' }}>
                  <DatePicker value={invoiceDate} onChange={setInvoiceDate} format="DD MMM YYYY" allowClear={false} size="small" locale={enLocale} />
                </td>
              </tr>
              <tr>
                <td style={{ color: '#888', paddingRight: 14, textAlign: 'right' }}>
                  <Checkbox checked={showDueDate} onChange={(e) => setShowDueDate(e.target.checked)} style={{ marginRight: 4 }} />
                  Due Date
                </td>
                <td style={{ textAlign: 'right' }}>
                  {showDueDate && <DatePicker value={dueDate} onChange={setDueDate} format="DD MMM YYYY" allowClear={false} size="small" locale={enLocale} />}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* From / Bill To */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 18 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: BASE_FS, fontWeight: 700, color: '#222', marginBottom: 6 }}>From</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: BRAND_DARK, textTransform: 'uppercase', marginBottom: 4 }}>{FROM_COMPANY}</div>
          <div style={{ fontSize: BASE_FS, color: '#555', lineHeight: 1.8 }}>
            GST No: {GST_NO}<br />
            Phone: {PHONE}<br />
            Website: {WEBSITE}
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'right' }}>
          <div style={{ fontSize: BASE_FS, fontWeight: 700, color: '#222', marginBottom: 6 }}>Bill to</div>
          <Input
            value={billToName}
            onChange={(e) => setBillToName(e.target.value)}
            placeholder="Recipient name"
            style={{ textAlign: 'right', fontWeight: 700, color: BRAND_DARK, marginBottom: 4, fontSize: 15 }}
          />
          <Input
            value={billToEmail}
            onChange={(e) => setBillToEmail(e.target.value)}
            placeholder="Email"
            style={{ textAlign: 'right', fontSize: BASE_FS, marginBottom: 4 }}
          />
          <Input
            value={billToPhone}
            onChange={(e) => setBillToPhone(e.target.value)}
            placeholder="Phone"
            style={{ textAlign: 'right', fontSize: BASE_FS, marginBottom: 4 }}
          />
          <Input
            value={billToAddress}
            onChange={(e) => setBillToAddress(e.target.value)}
            placeholder="Address"
            style={{ textAlign: 'right', fontSize: BASE_FS }}
          />
        </div>
      </div>

      {/* Sales rep */}
      {salesName && (
        <div style={{ background: '#f0f8ff', borderLeft: `3px solid ${BRAND}`, padding: '10px 16px', borderRadius: '0 4px 4px 0', marginBottom: 14, fontSize: BASE_FS }}>
          <span style={{ color: '#888' }}>Sales Rep: </span>
          <strong style={{ color: BRAND_DARK }}>{salesName}</strong>
          {salesEmail ? <span style={{ color: '#666' }}> &nbsp;{salesEmail}</span> : null}
          {salesPhone ? <span style={{ color: '#666' }}> &nbsp;{salesPhone}</span> : null}
        </div>
      )}

      {/* Items table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
        <thead>
          <tr>
            <th style={{ ...cellTh, textAlign: 'left', borderRadius: '4px 0 0 0' }}>Description</th>
            <th style={{ ...cellTh, textAlign: 'center', width: 60 }}>Qty</th>
            <th style={{ ...cellTh, textAlign: 'right', width: 130 }}>Unit Price (NZD)</th>
            <th style={{ ...cellTh, textAlign: 'center', width: 60 }}>GST</th>
            <th style={{ ...cellTh, textAlign: 'right', width: 130 }}>Amount (NZD)</th>
            <th style={{ ...cellTh, width: 36, borderRadius: '0 4px 0 0' }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fbff' }}>
              <td style={{ ...cellTd, paddingLeft: 0 }}>
                <Input value={item.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Description" style={inputBorder} />
              </td>
              <td style={{ ...cellTd, textAlign: 'center' }}>
                <InputNumber min={1} value={item.quantity} onChange={(v) => updateItem(i, { quantity: Number(v) || 1 })} style={{ ...inputBorder, width: '100%' }} controls={false} />
              </td>
              <td style={{ ...cellTd, textAlign: 'right' }}>
                <InputNumber min={0} value={item.amount} onChange={(v) => updateItem(i, { amount: Number(v) || 0 })} style={{ ...inputBorder, width: '100%' }} controls={false} />
              </td>
              <td style={{ ...cellTd, textAlign: 'center' }}>
                <Checkbox checked={!!item.gst} onChange={(e) => updateItem(i, { gst: e.target.checked })} />
              </td>
              <td style={{ ...cellTd, textAlign: 'right', paddingRight: items.length > 1 ? 10 : 0 }} colSpan={items.length > 1 ? 1 : 2}>
                {((Number(item.amount) || 0) * (Number(item.quantity) || 1)).toFixed(2)}
              </td>
              {items.length > 1 && (
                <td style={{ ...cellTd, textAlign: 'center' }}>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeItem(i)} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <Space style={{ marginBottom: 12 }}>
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addItem}>Add Line</Button>
      </Space>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <table style={{ width: 320 }}>
          <tbody>
            <tr><td style={{ fontSize: BASE_FS, color: '#666', padding: '6px 0' }}>Subtotal</td><td style={{ textAlign: 'right', fontSize: BASE_FS, padding: '6px 0' }}>{subtotal.toFixed(2)}</td></tr>
            <tr><td style={{ fontSize: BASE_FS, color: '#666', padding: '6px 0', borderBottom: '1px solid #e8ecf0' }}>Includes GST ({Number(gstPercent).toFixed(1)}%)</td><td style={{ textAlign: 'right', fontSize: BASE_FS, padding: '6px 0', borderBottom: '1px solid #e8ecf0' }}>{gstAmount.toFixed(2)}</td></tr>
            <tr style={{ background: BRAND }}><td style={{ padding: '8px 12px', fontSize: BASE_FS, fontWeight: 700, color: '#fff', borderRadius: '4px 0 0 4px' }}>Total (NZD)</td><td style={{ padding: '8px 12px', fontSize: BASE_FS, fontWeight: 700, color: '#fff', textAlign: 'right', borderRadius: '0 4px 4px 0' }}>{total.toFixed(2)}</td></tr>
            <tr><td style={{ fontSize: BASE_FS, color: '#666', padding: '6px 0' }}>Amount Paid</td><td style={{ textAlign: 'right', fontSize: BASE_FS, color: '#10b981', padding: '6px 0' }}>
              <InputNumber min={0} value={amountPaid} onChange={(v) => setAmountPaid(Number(v) || 0)} style={{ width: 130 }} controls={false} />
            </td></tr>
            <tr><td style={{ fontSize: BASE_FS, fontWeight: 700, padding: '6px 0' }}>Amount Due</td><td style={{ textAlign: 'right', fontSize: BASE_FS, fontWeight: 700, color: amountDue > 0 ? '#dc2626' : '#10b981', padding: '6px 0' }}>NZD {amountDue.toFixed(2)}</td></tr>
          </tbody>
        </table>
      </div>

      {/* Payment details with toggle —— 没有门店银行信息时整块不显示 */}
      <div style={{ borderTop: '1px solid #d6e8f8', paddingTop: 14, marginTop: 14 }}>
        {!hasBank ? (
          <Alert
            type="warning"
            showIcon
            message="未获取到门店银行账户，Invoice 上不会显示收款账户信息"
            description="请先在「门店管理」中为当前账号所属门店填写银行账号，或联系管理员为该账号关联门店。"
          />
        ) : (
        <div style={{ background: '#f7fbff', border: '1px solid #d6e8f8', borderRadius: 5, padding: '14px 18px', fontSize: BASE_FS, lineHeight: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: BASE_FS, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {paymentMode === 'domestic' ? 'Domestic Transfer (NZD)' : 'International Transfer'}
            </div>
            <Button size="small" icon={<SwapOutlined />} onClick={() => setPaymentMode(paymentMode === 'domestic' ? 'international' : 'domestic')}>
              Switch to {paymentMode === 'domestic' ? 'International' : 'Domestic'} Transfer
            </Button>
          </div>
          {paymentMode === 'domestic' ? (
            <div>
              {bankInfo.bankName && <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Bank: </span>{bankInfo.bankName}</div>}
              {bankInfo.bankAccountName && <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Account Name: </span>{bankInfo.bankAccountName}</div>}
              <div style={{ marginBottom: 6 }}><span style={{ color: '#888' }}>Account No.: </span><strong>{bankInfo.bankAccountNo}</strong></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: '#888' }}>Reference: </span>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="请输入EKOK号码" style={{ fontWeight: 700, flex: 1 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <span style={{ color: '#888' }}>Particulars: </span>
                <Input value={particulars} onChange={(e) => setParticulars(e.target.value)} placeholder="选填，留空则不显示在 invoice 上" style={{ fontWeight: 700, flex: 1 }} />
              </div>
            </div>
          ) : (
            <div>
              {bankInfo.international ? (
                <>
                  <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Beneficiary: </span>{bankInfo.international.beneficiaryName}</div>
                  <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Account No.: </span><strong>{bankInfo.international.accountNo}</strong></div>
                  <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Bank: </span>{bankInfo.international.bankName}</div>
                  <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Bank Address: </span>{bankInfo.international.bankAddress}</div>
                  <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>SWIFT: </span><strong>{bankInfo.international.swift}</strong></div>
                  <div style={{ marginBottom: 6 }}><span style={{ color: '#888' }}>Address: </span>{bankInfo.international.beneficiaryAddress}</div>
                </>
              ) : (
                <>
                  {bankInfo.bankName && <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Bank: </span>{bankInfo.bankName}</div>}
                  {bankInfo.bankSwift && <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>SWIFT: </span><strong>{bankInfo.bankSwift}</strong></div>}
                  <div style={{ marginBottom: 4 }}><span style={{ color: '#888' }}>Account No.: </span><strong>{bankInfo.bankAccountNo}</strong></div>
                  <div style={{ marginBottom: 6 }}><span style={{ color: '#888' }}>Bank Address: </span>23-29 Albert St, Auckland 1010, NZ</div>
                </>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: '#888' }}>Reference: </span>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="请输入EKOK号码" style={{ fontWeight: 700, flex: 1 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <span style={{ color: '#888' }}>Particulars: </span>
                <Input value={particulars} onChange={(e) => setParticulars(e.target.value)} placeholder="选填，留空则不显示在 invoice 上" style={{ fontWeight: 700, flex: 1 }} />
              </div>
            </div>
          )}
        </div>
        )}

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: BASE_FS, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Notes</div>
          <Input.TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="选填，可换行；留空则不显示在 invoice 上"
            autoSize={{ minRows: 3, maxRows: 8 }}
            style={{ fontSize: BASE_FS }}
          />
        </div>
      </div>
    </Modal>
  );
}
